/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';

import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';

import { BranchTyp } from 'src/app/_model/branch-types';
import { BranchTypesDaoService } from '../../dao/branch-types-dao.service';
import { BranchTypesService } from '../branch-types.service';
import { IMSId } from 'src/app/_model/ims-object';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads Branches
 */
@Injectable()
export class BranchTypesLogicService implements BranchTypesService {
  public branchTypes$ = new BehaviorSubject<BranchTyp[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private branchTypesDaoService: BranchTypesDaoService,
    private authenticationService: AuthenticationService,
  ) {
    this.initialize();
  }

  public getBranchTypeById(id: IMSId): BranchTyp {
    if (id === undefined) {
      return undefined;
    }

    return this.branchTypes$.getValue().find((bt) => {
      if (bt.id.imsId === id.imsId && bt.id.secondaryId === id.secondaryId) {
        return true;
      }
      return false;
    });
  }

  // Resetable
  public async reset() {
    this.branchTypesDaoService.reset();
  }

  public async clear() {
    await this.branchTypesDaoService.clear();
    this.branchTypes$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    await this.loadRemoteBranchTypes(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalBranchTypes();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions
  private async loadLocalBranchTypes() {
    try {
      this.logger.log(this.constructor.name, '- Load local BranchTypes');
      let branchTypes: BranchTyp[] =
        await this.branchTypesDaoService.getBranchTypes();

      this.branchTypes$.next(branchTypes);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local BranchTypes: ',
        error?.message,
      );
      throw error;
    }
  }

  private async loadRemoteBranchTypes(apiLevel: APILevel, syncKey: string) {
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(this.constructor.name, '- Load new BranchTypes from IMS');

      try {
        let branchTypes: BranchTyp[] = await this.loadRemoteBranchTypBatch(syncKey, []);

        this.logger.log(
          this.constructor.name,
          'Finished loading all branchTypes (' + branchTypes.length + ' Total)',
        );
        this.branchTypes$.next(branchTypes);
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading BranchTypes: ',
          error?.message,
        );
        throw error;
      }
    }
  }

  private async loadRemoteBranchTypBatch(
    syncKey: string,
    branchTypes: BranchTyp[],
    nextPart?: string,
  ): Promise<BranchTyp[]> {
    let branchTypesData;
    if (nextPart) {
      branchTypesData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncnextpart',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            nextid: nextPart,
            synckey: syncKey
          },
        }),
      );
    } else {
      branchTypesData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncbranchtypes',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            synckey: syncKey
          }
        }),
      );
    }

    let nextId: string;
    let nextBranchTypes: BranchTyp[] = [];
    for (const branchTypData of branchTypesData) {
      if (branchTypData['#nextid']) {
        nextId = branchTypData['#nextid'];
      } else {
        nextBranchTypes.push(BranchTyp.fromDto(branchTypData));
      }
    }

    this.logger.log(
      this.constructor.name,
      'Downloaded Batch BranchTypes: ',
      nextBranchTypes.length,
      'Total',
    );

    await this.branchTypesDaoService.saveBranchTypes(nextBranchTypes);

    let returnBranchTypes = branchTypes.concat(nextBranchTypes);
    if (nextId) {
      this.logger.log(
        this.constructor.name,
        'Continue to download more branchTypes with nextid',
        nextId,
      );
      return await this.loadRemoteBranchTypBatch(syncKey, returnBranchTypes, nextId);
    }

    return returnBranchTypes;
  }

  async initializeCompleted(): Promise<void> {}
}
